/**
 * ChartCard - Reusable card wrapper for chart components.
 * Provides consistent styling, loading states, error handling, and empty states.
 */
import { ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, BarChart3 } from 'lucide-react'
import { chartDefaults } from './chartConfig'
import { ChartSkeleton } from './ChartSkeleton'
import { cn } from '@/lib/utils'

export interface ChartCardProps {
  /** Chart title displayed in card header */
  title: string
  /** Optional description below title */
  description?: string
  /** Optional action element (button, link) in header */
  action?: ReactNode
  /** Chart content to render */
  children: ReactNode
  /** Whether the chart data is loading */
  isLoading?: boolean
  /** Error message to display */
  error?: string | null
  /** Whether the data is empty */
  isEmpty?: boolean
  /** Custom message for empty state */
  emptyMessage?: string
  /** Chart height in pixels */
  height?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Card wrapper for chart components with built-in loading, error, and empty states.
 * 
 * @example
 * ```tsx
 * <ChartCard title="Runs Over Time" isLoading={isLoading} error={error}>
 *   <RunsOverTimeChart data={data} />
 * </ChartCard>
 * ```
 */
export function ChartCard({
  title,
  description,
  action,
  children,
  isLoading = false,
  error = null,
  isEmpty = false,
  emptyMessage = 'No data available',
  height = chartDefaults.height,
  className,
}: ChartCardProps) {
  const renderContent = () => {
    // Loading state
    if (isLoading) {
      return <ChartSkeleton height={height} />
    }

    // Error state
    if (error) {
      return (
        <div 
          className="flex items-center justify-center" 
          style={{ height }}
        >
          <Alert variant="destructive" className="max-w-sm">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )
    }

    // Empty state
    if (isEmpty) {
      return (
        <div 
          className="flex flex-col items-center justify-center text-muted-foreground"
          style={{ height }}
        >
          <BarChart3 className="h-12 w-12 mb-3 opacity-50" />
          <p className="text-sm">{emptyMessage}</p>
        </div>
      )
    }

    // Normal content
    return (
      <div style={{ height }}>
        {children}
      </div>
    )
  }

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base font-medium" role="heading" aria-level={3}>
            {title}
          </CardTitle>
          {description && (
            <CardDescription className="text-xs mt-1">
              {description}
            </CardDescription>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </CardHeader>
      <CardContent>
        <figure 
          role="figure" 
          aria-label={`${title} chart`}
        >
          {renderContent()}
        </figure>
      </CardContent>
    </Card>
  )
}
